import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AplicationService } from '@services/aplication/aplication.service';
import { Observable, throwError } from 'rxjs';
import { CampaignsOffersRepository } from './campaigns-offers.repository';
import { GetAvailableOffersResponse, GetOffersPaginatedResponse, OfferResponse } from '../domain/offer.model';
import { CreateOfferDto } from './dtos/create-offer.dto';
import { UpdateOfferDto } from './dtos/update-offer.dto';

@Injectable({
    providedIn: 'root',
})
export class CampaignsOffersApiService implements CampaignsOffersRepository {
    private readonly http = inject(HttpClient);
    private readonly aplicationService = inject(AplicationService);

    /**
     * Builds the roads-scoped base URL and invokes `request` with it. When no aplication
     * is selected, fails fast by returning an Observable error instead of throwing
     * synchronously, so callers' existing `catchError`/`finalize` pipelines still run.
     */
    private withOffersBaseUrl<T>(request: (baseUrl: string) => Observable<T>): Observable<T> {
        const roadId = this.aplicationService.selectedRoadId();

        if (!roadId) {
            return throwError(() => new Error('Cannot build offers request URL: no aplication selected'));
        }

        return request(`${environment.url}/prism-admin-campaigns-offers/v2/roads/${roadId}/offers`);
    }

    createOffer(request: CreateOfferDto): Observable<OfferResponse> {
        return this.withOffersBaseUrl((baseUrl) => this.http.post<OfferResponse>(baseUrl, request, {}));
    }

    getOffers(page: number, size: number): Observable<GetOffersPaginatedResponse> {
        let params = new HttpParams();
        if (page) {
            params = params.set('page', page.toString());
        }
        if (size) {
            params = params.set('size', size.toString());
        }

        return this.withOffersBaseUrl((baseUrl) => this.http.get<GetOffersPaginatedResponse>(baseUrl, { params }));
    }

    getAvailableOffers(): Observable<GetAvailableOffersResponse> {
        return this.withOffersBaseUrl((baseUrl) => this.http.get<GetAvailableOffersResponse>(`${baseUrl}/available`));
    }

    getOffer(id: string): Observable<OfferResponse> {
        return this.withOffersBaseUrl((baseUrl) => this.http.get<OfferResponse>(`${baseUrl}/${id}`));
    }

    updateOffer(id: string, request: UpdateOfferDto): Observable<OfferResponse> {
        return this.withOffersBaseUrl((baseUrl) => this.http.patch<OfferResponse>(`${baseUrl}/${id}`, request));
    }
}
